// frontend/src/components/LeadForm.jsx
// Updated: March 22, 2025
import React, { useState, useEffect, useCallback } from 'react';
import { getLocationFromZip } from 'api'; // Make sure to import this
import { US_STATES } from '../constants'; // Add US_STATES import

// Add this function to calculate price on the frontend side
const calculateLeadPrice = (investmentAmount) => {
  if (!investmentAmount) return 150; // Default price
  
  // Handle string with currency symbols and commas
  const cleanValue = typeof investmentAmount === 'string' 
    ? investmentAmount.replace(/[^0-9.-]/g, '') 
    : investmentAmount;
  
  const numValue = parseFloat(cleanValue);
  
  // Check if conversion resulted in a valid number
  if (isNaN(numValue) || numValue <= 0) return 150;
  
  // Apply pricing tiers
  if (numValue >= 1000000) return 400;  // Over $1,000,000
  if (numValue >= 500000) return 325;   // $500,001 - $1,000,000
  if (numValue >= 250000) return 275;   // $250,001 - $500,000
  if (numValue >= 100000) return 225;   // $100,001 - $250,000
  if (numValue >= 25000) return 150;    // $25,000 - $100,000
  return 150; // Default for small investments
};

const LeadForm = ({ lead = null, onSubmit, submitLabel = 'Save', disabled }) => {
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    email: '',
    phone: '',
    state: '',
    zipCode: '',
    city: '', 
    status: 'available',
    price: 150, // Updated from 50 to 150 to match our default price
    age: '',
    year_born: '', // Added year_born field
    submitter_ip: '',
    unbounce_page_id: '',
    unbounce_page_name: '',
    unbounce_page_url: '',
    domain: '',
    variant_id: '',
    page_state: '',
    last_published_at: '',
    conversion_rate: '',
    request_id: '',
    created_by: '',
    investment_amount: '',
    annuity_type: '',
    annuity_type_preference: '',
    term_length: '',
    income_start_time: '',
    retirement_timeline: '',
    primary_goal: '',
    financial_comfort_level: '',
    contact_preference: '',
    campaign_id: '',
    ad_group_id: '',
    ad_id: '',
    utm_medium: '',
    utm_source: '',
    utm_campaign: '',
    utm_content: '',
    utm_term: '',
    unbounce_account_id: '',
    unbounce_account_name: '',
    unbounce_sub_account_id: '',
    unbounce_sub_account_name: '',
  });
  const [errors, setErrors] = useState({});
  const [locationLoading, setLocationLoading] = useState(false); // Added for location loading state

  // Populate form with lead data if provided
  useEffect(() => {
    if (lead) {
      const currentYear = new Date().getFullYear();
      const yearBorn = lead.age ? currentYear - Number(lead.age) : '';
      setFormData({
        id: lead.id || '',
        name: lead.name || '',
        email: lead.email || '',
        phone: lead.phone || '',
        state: lead.state || '',
        zipCode: lead.zip_code || '',
        city: lead.city || '',
        status: lead.status || 'available',
        price: lead.price || 50,
        age: lead.age || '',
        year_born: yearBorn,
        submitter_ip: lead.submitter_ip || '',
        unbounce_page_id: lead.unbounce_page_id || '',
        unbounce_page_name: lead.unbounce_page_name || '',
        unbounce_page_url: lead.unbounce_page_url || '',
        domain: lead.domain || '',
        variant_id: lead.variant_id || '',
        page_state: lead.page_state || '',
        last_published_at: lead.last_published_at || '',
        conversion_rate: lead.conversion_rate || '',
        request_id: lead.request_id || '',
        created_by: lead.created_by || '',
        investment_amount: lead.investment_amount || '',
        annuity_type: lead.annuity_type || '',
        annuity_type_preference: lead.annuity_type_preference || '',
        term_length: lead.term_length || '',
        income_start_time: lead.income_start_time || '',
        retirement_timeline: lead.retirement_timeline || '',
        primary_goal: lead.primary_goal || '',
        financial_comfort_level: lead.financial_comfort_level || '',
        contact_preference: lead.contact_preference || '',
        campaign_id: lead.campaign_id || '',
        ad_group_id: lead.ad_group_id || '',
        ad_id: lead.ad_id || '',
        utm_medium: lead.utm_medium || '',
        utm_source: lead.utm_source || '',
        utm_campaign: lead.utm_campaign || '',
        utm_content: lead.utm_content || '',
        utm_term: lead.utm_term || '',
        unbounce_account_id: lead.unbounce_account_id || '',
        unbounce_account_name: lead.unbounce_account_name || '',
        unbounce_sub_account_id: lead.unbounce_sub_account_id || '',
        unbounce_sub_account_name: lead.unbounce_sub_account_name || '',
      });
    }
  }, [lead]);

  // Handle input changes
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    
    setFormData(prev => {
      const updatedForm = {
        ...prev,
        [name]: value
      };
      
      // If investment amount changes, recalculate the price
      if (name === 'investment_amount') {
        updatedForm.price = calculateLeadPrice(value);
      }
      
      return updatedForm;
    });
  }, []); // Empty dependency array since we're using functional updates

  // Handle zip code change with automatic location detection
  const handleZipCodeChange = useCallback(async (e) => {
    const { value } = e.target;
    const zipRegex = /^\d{5}$/;
    
    // Update the zip code in the form
    handleChange(e);
    
    // If valid zip code format, try to get city and state
    if (zipRegex.test(value)) {
      setLocationLoading(true);
      try {
        const result = await getLocationFromZip(value);
        if (result.success && result.data) {
          // Ensure we're using the 2-letter state code
          const stateCode = result.data.state.length === 2 ? 
            result.data.state.toUpperCase() : 
            Object.entries(US_STATES).find(([_, name]) => 
              name.toLowerCase() === result.data.state.toLowerCase()
            )?.[0] || '';

          setFormData(prev => ({
            ...prev,
            state: stateCode,
            city: result.data.city
          }));
        }
      } catch (error) {
        console.error('Error fetching location data:', error);
        setErrors(prev => ({ ...prev, zipCode: 'Failed to fetch location data' }));
      } finally {
        setLocationLoading(false);
      }
    }
  }, [handleChange, setFormData, setErrors, setLocationLoading]);

  // Add effect to update state when zip code changes
  useEffect(() => {
    if (formData.zipCode && /^\d{5}$/.test(formData.zipCode)) {
      handleZipCodeChange({ target: { value: formData.zipCode } });
    }
  }, [formData.zipCode, handleZipCodeChange]);

  // Validate form data
  const validate = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = 'Name is required';
    if (!formData.email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.zipCode) newErrors.zipCode = 'Zip code is required';
    else if (!/^\d{5}$/.test(formData.zipCode)) newErrors.zipCode = 'Invalid zip code format';
    if (!formData.state) newErrors.state = 'State is required';
    if (formData.price < 0) newErrors.price = 'Price cannot be negative';
    if (formData.conversion_rate && (formData.conversion_rate < 0 || formData.conversion_rate > 100)) newErrors.conversion_rate = 'Conversion rate must be between 0 and 100';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      // Transform form data to match backend field names
      const transformedData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        state: formData.state,
        zip_code: formData.zipCode,
        age: formData.age ? Number(formData.age) : null,
        investment_amount: formData.investment_amount ? Number(formData.investment_amount) : null,
        annuity_type: formData.annuity_type,
        status: formData.status,
        price: formData.price ? Number(formData.price) : 50,
        unbounce_page_name: formData.unbounce_page_name,
        unbounce_lead_id: formData.unbounce_lead_id,
        conversion_rate: formData.conversion_rate ? Number(formData.conversion_rate) : null,
        submitter_ip: formData.submitter_ip,
        notes: formData.notes
      };
      onSubmit(transformedData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="lead-form space-y-4 sm:space-y-6">
      {/* Lead Identification */}
      <div className="form-group">
        <label htmlFor="id" className="text-gray-700 text-sm sm:text-base">Lead ID</label>
        <input
          type="text"
          id="id"
          name="id"
          value={formData.id}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Lead ID"
          disabled={true}
        />
      </div>
      <div className="form-group">
        <label htmlFor="name" className="text-gray-700 text-sm sm:text-base">Name</label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Lead name"
          disabled={disabled}
        />
        {errors.name && <span className="text-red-500 text-sm">{errors.name}</span>}
      </div>
      <div className="form-group">
        <label htmlFor="email" className="text-gray-700 text-sm sm:text-base">Email</label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Lead email"
          disabled={disabled}
        />
        {errors.email && <span className="text-red-500 text-sm">{errors.email}</span>}
      </div>
      <div className="form-group">
        <label htmlFor="phone" className="text-gray-700 text-sm sm:text-base">Phone</label>
        <input
          type="text"
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Lead phone"
          disabled={disabled}
        />
      </div>
      <div className="form-group">
        <label htmlFor="zipCode" className="text-gray-700 text-sm sm:text-base">Zip Code</label>
        <div className="relative">
          <input
            type="text"
            id="zipCode"
            name="zipCode"
            value={formData.zipCode}
            onChange={handleZipCodeChange}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
            aria-label="Lead zip code"
            disabled={disabled || locationLoading}
            maxLength={5}
            placeholder="Enter zip code"
          />
          {locationLoading && (
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
              <span className="h-4 w-4 block rounded-full border-2 border-t-blue-500 border-r-transparent border-b-transparent border-l-transparent animate-spin"></span>
            </div>
          )}
        </div>
        <p className="mt-1 text-xs text-gray-500">
          Enter a zip code to automatically determine city and state
        </p>
        {errors.zipCode && <span className="text-red-500 text-sm">{errors.zipCode}</span>}
      </div>
      <div className="form-group">
        <label htmlFor="city" className="text-gray-700 text-sm sm:text-base">City</label>
        <div className="relative">
          <input
            type="text"
            id="city"
            name="city"
            value={formData.city || ''}
            onChange={handleChange}
            className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base ${locationLoading ? 'bg-gray-100' : ''}`}
            aria-label="Lead city"
            disabled={true}
            style={{ WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none' }}
            readOnly
          />
          {locationLoading && (
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
              <span className="h-4 w-4 block rounded-full border-2 border-t-blue-500 border-r-transparent border-b-transparent border-l-transparent animate-spin"></span>
            </div>
          )}
        </div>
      </div>
      <div className="form-group">
        <label htmlFor="state" className="text-gray-700 text-sm sm:text-base">State</label>
        <select
          id="state"
          name="state"
          value={formData.state || ''}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Lead state"
          disabled={disabled}
        >
          <option value="">Select State</option>
          {Object.entries(US_STATES).map(([code, name]) => (
            <option key={code} value={code}>
              {name} ({code})
            </option>
          ))}
        </select>
        {errors.state && <span className="text-red-500 text-sm">{errors.state}</span>}
      </div>
      <div className="form-group">
        <label htmlFor="age" className="text-gray-700 text-sm sm:text-base">Age</label>
        <input
          type="number"
          id="age"
          name="age"
          value={formData.age}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Lead age"
          disabled={disabled}
        />
      </div>
      <div className="form-group">
        <label htmlFor="year_born" className="text-gray-700 text-sm sm:text-base">Year Born In</label>
        <input
          type="number"
          id="year_born"
          name="year_born"
          value={formData.year_born}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Lead year born"
          disabled={disabled}
          min="1900"
          max={new Date().getFullYear()}
        />
      </div>
      <div className="form-group">
        <label htmlFor="submitter_ip" className="text-gray-700 text-sm sm:text-base">Submitter IP</label>
        <input
          type="text"
          id="submitter_ip"
          name="submitter_ip"
          value={formData.submitter_ip}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Submitter IP"
          disabled={disabled}
        />
      </div>

      {/* Page Details */}
      <div className="form-group">
        <label htmlFor="unbounce_page_id" className="text-gray-700 text-sm sm:text-base">Page ID</label>
        <input
          type="text"
          id="unbounce_page_id"
          name="unbounce_page_id"
          value={formData.unbounce_page_id}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Page ID"
          disabled={disabled}
        />
      </div>
      <div className="form-group">
        <label htmlFor="unbounce_page_name" className="text-gray-700 text-sm sm:text-base">Page Name</label>
        <input
          type="text"
          id="unbounce_page_name"
          name="unbounce_page_name"
          value={formData.unbounce_page_name}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Page Name"
          disabled={disabled}
        />
      </div>
      <div className="form-group">
        <label htmlFor="unbounce_page_url" className="text-gray-700 text-sm sm:text-base">Page URL</label>
        <input
          type="text"
          id="unbounce_page_url"
          name="unbounce_page_url"
          value={formData.unbounce_page_url}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Page URL"
          disabled={disabled}
        />
      </div>
      <div className="form-group">
        <label htmlFor="domain" className="text-gray-700 text-sm sm:text-base">Domain</label>
        <input
          type="text"
          id="domain"
          name="domain"
          value={formData.domain}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Domain"
          disabled={disabled}
        />
      </div>
      <div className="form-group">
        <label htmlFor="variant_id" className="text-gray-700 text-sm sm:text-base">Variant ID</label>
        <input
          type="text"
          id="variant_id"
          name="variant_id"
          value={formData.variant_id}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Variant ID"
          disabled={disabled}
        />
      </div>
      <div className="form-group">
        <label htmlFor="page_state" className="text-gray-700 text-sm sm:text-base">Page State</label>
        <select
          id="page_state"
          name="page_state"
          value={formData.page_state}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Page State"
          disabled={disabled}
        >
          <option value="">Select State</option>
          <option value="published">Published</option>
          <option value="unpublished">Unpublished</option>
        </select>
      </div>
      <div className="form-group">
        <label htmlFor="last_published_at" className="text-gray-700 text-sm sm:text-base">Last Published At</label>
        <input
          type="datetime-local"
          id="last_published_at"
          name="last_published_at"
          value={formData.last_published_at ? new Date(formData.last_published_at).toISOString().slice(0, 16) : ''}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Last Published At"
          disabled={disabled}
        />
      </div>
      <div className="form-group">
        <label htmlFor="conversion_rate" className="text-gray-700 text-sm sm:text-base">Conversion Rate (%)</label>
        <input
          type="number"
          id="conversion_rate"
          name="conversion_rate"
          value={formData.conversion_rate}
          onChange={handleChange}
          min="0"
          max="100"
          step="0.01"
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Conversion Rate"
          disabled={disabled}
        />
        {errors.conversion_rate && <span className="text-red-500 text-sm">{errors.conversion_rate}</span>}
      </div>

      {/* Extra Data */}
      <div className="form-group">
        <label htmlFor="request_id" className="text-gray-700 text-sm sm:text-base">Request ID</label>
        <input
          type="text"
          id="request_id"
          name="request_id"
          value={formData.request_id}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Request ID"
          disabled={disabled}
        />
      </div>
      <div className="form-group">
        <label htmlFor="created_by" className="text-gray-700 text-sm sm:text-base">Created By</label>
        <select
          id="created_by"
          name="created_by"
          value={formData.created_by}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Created By"
          disabled={disabled}
        >
          <option value="">Unknown</option>
          <option value="web">Web</option>
          <option value="api">API</option>
        </select>
      </div>

      {/* Investment Details */}
      <div className="form-group">
        <label htmlFor="investment_amount" className="text-gray-700 text-sm sm:text-base">Investment Amount</label>
        <input
          type="text"
          id="investment_amount"
          name="investment_amount"
          value={formData.investment_amount || ''}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Lead investment amount"
          placeholder="e.g. $25,000 - $100,000"
          disabled={disabled}
        />
      </div>
      <div className="form-group">
        <label htmlFor="annuity_type" className="text-gray-700 text-sm sm:text-base">Annuity Type</label>
        <input
          type="text"
          id="annuity_type"
          name="annuity_type"
          value={formData.annuity_type}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Lead annuity type"
          disabled={disabled}
        />
      </div>
      <div className="form-group">
        <label htmlFor="annuity_type_preference" className="text-gray-700 text-sm sm:text-base">Annuity Type Preference</label>
        <input
          type="text"
          id="annuity_type_preference"
          name="annuity_type_preference"
          value={formData.annuity_type_preference}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Lead annuity type preference"
          disabled={disabled}
        />
      </div>
      <div className="form-group">
        <label htmlFor="term_length" className="text-gray-700 text-sm sm:text-base">Term Length</label>
        <input
          type="text"
          id="term_length"
          name="term_length"
          value={formData.term_length}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Lead term length"
          disabled={disabled}
        />
      </div>

      {/* Income & Retirement */}
      <div className="form-group">
        <label htmlFor="income_start_time" className="text-gray-700 text-sm sm:text-base">Income Start Time</label>
        <input
          type="text"
          id="income_start_time"
          name="income_start_time"
          value={formData.income_start_time}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Lead income start time"
          disabled={disabled}
        />
      </div>
      <div className="form-group">
        <label htmlFor="retirement_timeline" className="text-gray-700 text-sm sm:text-base">Retirement Timeline</label>
        <input
          type="text"
          id="retirement_timeline"
          name="retirement_timeline"
          value={formData.retirement_timeline}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Lead retirement timeline"
          disabled={disabled}
        />
      </div>

      {/* Annuity Preferences */}
      <div className="form-group">
        <label htmlFor="primary_goal" className="text-gray-700 text-sm sm:text-base">Primary Goal</label>
        <input
          type="text"
          id="primary_goal"
          name="primary_goal"
          value={formData.primary_goal}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Lead primary goal"
          disabled={disabled}
        />
      </div>
      <div className="form-group">
        <label htmlFor="financial_comfort_level" className="text-gray-700 text-sm sm:text-base">Financial Comfort Level</label>
        <input
          type="text"
          id="financial_comfort_level"
          name="financial_comfort_level"
          value={formData.financial_comfort_level}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Lead financial comfort level"
          disabled={disabled}
        />
      </div>
      <div className="form-group">
        <label htmlFor="contact_preference" className="text-gray-700 text-sm sm:text-base">Contact Preference</label>
        <input
          type="text"
          id="contact_preference"
          name="contact_preference"
          value={formData.contact_preference}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Lead contact preference"
          disabled={disabled}
        />
      </div>

      {/* Marketing Fields */}
      <div className="form-group">
        <label htmlFor="campaign_id" className="text-gray-700 text-sm sm:text-base">Campaign ID</label>
        <input
          type="text"
          id="campaign_id"
          name="campaign_id"
          value={formData.campaign_id}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Lead campaign ID"
          disabled={disabled}
        />
      </div>
      <div className="form-group">
        <label htmlFor="ad_group_id" className="text-gray-700 text-sm sm:text-base">Ad Group ID</label>
        <input
          type="text"
          id="ad_group_id"
          name="ad_group_id"
          value={formData.ad_group_id}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Lead ad group ID"
          disabled={disabled}
        />
      </div>
      <div className="form-group">
        <label htmlFor="ad_id" className="text-gray-700 text-sm sm:text-base">Ad ID</label>
        <input
          type="text"
          id="ad_id"
          name="ad_id"
          value={formData.ad_id}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Lead ad ID"
          disabled={disabled}
        />
      </div>
      <div className="form-group">
        <label htmlFor="utm_medium" className="text-gray-700 text-sm sm:text-base">UTM Medium</label>
        <input
          type="text"
          id="utm_medium"
          name="utm_medium"
          value={formData.utm_medium}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Lead UTM medium"
          disabled={disabled}
        />
      </div>
      <div className="form-group">
        <label htmlFor="utm_source" className="text-gray-700 text-sm sm:text-base">UTM Source</label>
        <input
          type="text"
          id="utm_source"
          name="utm_source"
          value={formData.utm_source}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Lead UTM source"
          disabled={disabled}
        />
      </div>
      <div className="form-group">
        <label htmlFor="utm_campaign" className="text-gray-700 text-sm sm:text-base">UTM Campaign</label>
        <input
          type="text"
          id="utm_campaign"
          name="utm_campaign"
          value={formData.utm_campaign}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Lead UTM campaign"
          disabled={disabled}
        />
      </div>
      <div className="form-group">
        <label htmlFor="utm_content" className="text-gray-700 text-sm sm:text-base">UTM Content</label>
        <input
          type="text"
          id="utm_content"
          name="utm_content"
          value={formData.utm_content}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Lead UTM content"
          disabled={disabled}
        />
      </div>
      <div className="form-group">
        <label htmlFor="utm_term" className="text-gray-700 text-sm sm:text-base">UTM Term</label>
        <input
          type="text"
          id="utm_term"
          name="utm_term"
          value={formData.utm_term}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Lead UTM term"
          disabled={disabled}
        />
      </div>

      {/* Unbounce Account Fields */}
      <div className="form-group">
        <label htmlFor="unbounce_account_id" className="text-gray-700 text-sm sm:text-base">Unbounce Account ID</label>
        <input
          type="text"
          id="unbounce_account_id"
          name="unbounce_account_id"
          value={formData.unbounce_account_id}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Unbounce Account ID"
          disabled={disabled}
        />
      </div>
      <div className="form-group">
        <label htmlFor="unbounce_account_name" className="text-gray-700 text-sm sm:text-base">Unbounce Account Name</label>
        <input
          type="text"
          id="unbounce_account_name"
          name="unbounce_account_name"
          value={formData.unbounce_account_name}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Unbounce Account Name"
          disabled={disabled}
        />
      </div>
      <div className="form-group">
        <label htmlFor="unbounce_sub_account_id" className="text-gray-700 text-sm sm:text-base">Unbounce Sub-Account ID</label>
        <input
          type="text"
          id="unbounce_sub_account_id"
          name="unbounce_sub_account_id"
          value={formData.unbounce_sub_account_id}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Unbounce Sub-Account ID"
          disabled={disabled}
        />
      </div>
      <div className="form-group">
        <label htmlFor="unbounce_sub_account_name" className="text-gray-700 text-sm sm:text-base">Unbounce Sub-Account Name</label>
        <input
          type="text"
          id="unbounce_sub_account_name"
          name="unbounce_sub_account_name"
          value={formData.unbounce_sub_account_name}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Unbounce Sub-Account Name"
          disabled={disabled}
        />
      </div>

      {/* Core Fields */}
      <div className="form-group">
        <label htmlFor="status" className="text-gray-700 text-sm sm:text-base">Status</label>
        <select
          id="status"
          name="status"
          value={formData.status}
          onChange={handleChange}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Lead status"
          disabled={disabled}
        >
          <option value="available">Available</option>
          <option value="purchased">Purchased</option>
          <option value="archived">Archived</option>
        </select>
      </div>
      <div className="form-group">
        <label htmlFor="price" className="text-gray-700 text-sm sm:text-base">Price ($)</label>
        <input
          type="number"
          id="price"
          name="price"
          value={formData.price}
          onChange={handleChange}
          min="0"
          step="0.01"
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm sm:text-base"
          aria-label="Lead price"
          disabled={disabled}
        />
        {errors.price && <span className="text-red-500 text-sm">{errors.price}</span>}
      </div>

      <div className="form-actions">
        <button type="submit" className="btn btn-primary text-sm sm:text-base" disabled={disabled}>
          {submitLabel}
        </button>
      </div>
    </form>
  );
};

export default LeadForm;